/**
 * DevModeIndicator - Floating dev mode panel
 * 
 * Shows a toggle button when dev mode is NOT active (local dev/staging only)
 * Shows current mock user info when dev mode IS active
 * Provides role switching and quick navigation
 * 
 * ⚠️ SECURITY: Only renders on localhost and staging environments
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevMode, isDevModeAllowed } from './DevModeContext';
import { getMockUsersForApp } from './mock-users';
import type { DevModeRole, AiAmpDevRole, MarketingDevRole, RoleConfig } from './types';
import { 
  X, 
  Wrench, 
  Wallet, 
  ChevronUp, 
  ChevronDown,
  User,
  Code2,
  Building2,
  Shield,
  LogIn,
  LayoutDashboard,
  Plug,
  Eye,
  UserPlus,
  Briefcase,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';

// Console routes for each role
const CONSOLE_ROUTES: Record<string, { path: string; label: string }> = {
  creator: { path: '/console/creator', label: 'Creator Console' },
  developer: { path: '/console/developer', label: 'Developer Portal' },
  brand: { path: '/console/brand', label: 'Brand Console' },
  admin: { path: '/console/admin', label: 'Admin Console' },
};

// AI-AMP domain for cross-app navigation
const getAiAmpDomain = (): string => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5174';
  }
  if (hostname.includes('staging')) {
    return 'https://staging.lykbl.ai';
  }
  return 'https://lykbl.ai';
};

// Role configurations for AI-AMP
const AI_AMP_ROLE_CONFIG: Record<AiAmpDevRole, RoleConfig> = {
  creator: { 
    icon: User, 
    label: 'Creator', 
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    description: 'Content creator with unified analytics',
  },
  developer: { 
    icon: Code2, 
    label: 'Developer', 
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    description: 'Agent/workflow developer',
  },
  brand: { 
    icon: Building2, 
    label: 'Brand', 
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    description: 'Brand/advertiser account',
  },
  admin: { 
    icon: Shield, 
    label: 'Admin', 
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    description: 'Platform administrator',
  },
};

// Role configurations for Marketing
const MARKETING_ROLE_CONFIG: Record<MarketingDevRole, RoleConfig> = {
  visitor: { 
    icon: Eye, 
    label: 'Visitor', 
    color: 'text-gray-500',
    bgColor: 'bg-gray-500/10',
    description: 'Unauthenticated site visitor',
  },
  'creator-prospect': { 
    icon: UserPlus, 
    label: 'Creator Prospect', 
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    description: 'Potential creator exploring platform',
  },
  'brand-prospect': { 
    icon: Briefcase, 
    label: 'Brand Prospect', 
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    description: 'Potential brand/advertiser',
  },
  creator: { 
    icon: User, 
    label: 'Creator', 
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    description: 'Go to Creator Console',
  },
  brand: { 
    icon: Building2, 
    label: 'Brand', 
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    description: 'Go to Brand Console',
  },
  admin: { 
    icon: Shield, 
    label: 'Admin', 
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    description: 'Global Admin Console',
  },
};

export interface DevModeIndicatorProps {
  /** Custom quick links for the panel */
  quickLinks?: Array<{ label: string; path: string; icon?: React.ComponentType<{ className?: string }> }>;
}

const DevModeIndicatorInner: React.FC<DevModeIndicatorProps> = ({ quickLinks }) => {
  const { isDevMode, devUser, devRole, setDevRole, exitDevMode, appType } = useDevMode();
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const mockUsers = getMockUsersForApp(appType);
  const roleConfig = appType === 'ai-amp' ? AI_AMP_ROLE_CONFIG : MARKETING_ROLE_CONFIG;
  
  // Determine environment label for the badge
  const getEnvLabel = (): string => {
    const appEnv = import.meta.env.VITE_APP_ENVIRONMENT;
    if (appEnv === 'staging') return 'STAGING';
    if (import.meta.env.DEV) return 'LOCAL';
    return 'DEV';
  };

  // Default quick links based on app type
  const defaultQuickLinks = appType === 'ai-amp' 
    ? [
        { label: 'Dashboard', path: '/app', icon: LayoutDashboard },
        { label: 'Integrations', path: '/app/integrations', icon: Plug },
      ]
    : [
        { label: 'Home', path: '/', icon: LayoutDashboard },
        { label: 'Pricing', path: '/pricing', icon: Briefcase },
      ];
  
  const linksToShow = quickLinks || defaultQuickLinks;

  const handleRoleSelect = (role: DevModeRole) => {
    setDevRole(role);
    const defaultPath = appType === 'ai-amp' ? '/app' : '/';
    navigate(`${defaultPath}?devMode=${role}`);
  };

  const handleQuickNav = (path: string) => {
    if (devRole) {
      navigate(`${path}?devMode=${devRole}`);
    } else {
      navigate(path);
    }
  };

  // Collapsed button - using native elements for reliable styling
  if (!isExpanded) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '16px',
          zIndex: 99999,
        }}
      >
        <button
          onClick={() => setIsExpanded(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '12px',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            background: isDevMode 
              ? 'linear-gradient(to right, #f59e0b, #ea580c)' 
              : 'linear-gradient(to right, #374151, #1f2937)',
          }}
        >
          <Wrench style={{ width: '16px', height: '16px' }} />
          {isDevMode ? (
            <span>Dev: {devRole}</span>
          ) : (
            <span>Dev Mode</span>
          )}
          <ChevronUp style={{ width: '12px', height: '12px' }} />
        </button>
      </div>
    );
  }

  // Expanded panel
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '16px',
        left: '16px',
        zIndex: 99999,
        width: '288px',
      }}
    >
      <div 
        style={{ 
          backgroundColor: '#1f2937', 
          borderColor: '#374151', 
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div 
          style={{
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isDevMode 
              ? 'linear-gradient(to right, #f59e0b, #ea580c)'
              : 'linear-gradient(to right, #374151, #1f2937)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
            <Wrench className="h-4 w-4" />
            <span style={{ fontWeight: 600, fontSize: '14px' }}>Dev Mode</span>
            <span style={{ 
              fontSize: '10px', 
              padding: '2px 6px', 
              borderRadius: '4px',
              backgroundColor: 'rgba(255,255,255,0.2)',
            }}>
              {getEnvLabel()}
            </span>
            <span style={{ 
              fontSize: '10px', 
              padding: '2px 6px', 
              borderRadius: '4px',
              backgroundColor: 'rgba(255,255,255,0.2)',
            }}>
              {appType}
            </span>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              color: 'white',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Active Session */}
          {isDevMode && devUser ? (
            <div style={{ 
              padding: '12px', 
              borderRadius: '8px', 
              backgroundColor: 'rgba(249, 115, 22, 0.1)',
              border: '1px solid rgba(249, 115, 22, 0.2)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>Logged in as</span>
                <button
                  onClick={exitDevMode}
                  style={{
                    padding: '2px 8px',
                    fontSize: '10px',
                    color: '#9ca3af',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <X className="h-3 w-3" />
                  Exit
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img 
                  src={devUser.avatarUrl} 
                  alt={devUser.displayName}
                  style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '14px', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{devUser.displayName}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{devUser.email}</div>
                </div>
              </div>
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                <span style={{ 
                  padding: '2px 8px', 
                  borderRadius: '4px', 
                  backgroundColor: '#374151',
                  color: 'white',
                  fontSize: '10px',
                }}>
                  {devUser.role}
                </span>
                {devUser.walletBalance > 0 && (
                  <span style={{ color: '#f97316', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Wallet className="h-3 w-3" />
                    {devUser.walletBalance.toLocaleString()} AC
                  </span>
                )}
              </div>
              
              {/* Go to Console Button - only for console-enabled roles */}
              {CONSOLE_ROUTES[devUser.role] && (
                <button
                  onClick={() => {
                    const consoleInfo = CONSOLE_ROUTES[devUser.role];
                    if (appType === 'marketing') {
                      // Marketing app: Open AI-AMP console in same tab
                      const aiAmpUrl = `${getAiAmpDomain()}${consoleInfo.path}?devMode=${devUser.role}`;
                      window.location.href = aiAmpUrl;
                    } else {
                      // AI-AMP app: Navigate directly
                      navigate(`${consoleInfo.path}?devMode=${devUser.role}`);
                    }
                  }}
                  style={{
                    marginTop: '10px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '13px',
                    color: 'white',
                    background: 'linear-gradient(to right, #f97316, #ea580c)',
                    boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.3)';
                  }}
                >
                  {appType === 'marketing' ? (
                    <>
                      <ExternalLink style={{ width: '14px', height: '14px' }} />
                      Go to {CONSOLE_ROUTES[devUser.role].label}
                    </>
                  ) : (
                    <>
                      <ArrowRight style={{ width: '14px', height: '14px' }} />
                      Go to {CONSOLE_ROUTES[devUser.role].label}
                    </>
                  )}
                </button>
              )}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '8px 12px', 
              borderRadius: '8px', 
              backgroundColor: 'rgba(55, 65, 81, 0.5)',
            }}>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                Select a role to test the platform
              </p>
            </div>
          )}

          {/* Role Selection */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af', paddingLeft: '4px' }}>Quick Login</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              {(Object.keys(roleConfig) as DevModeRole[]).map((role) => {
                const config = roleConfig[role as keyof typeof roleConfig];
                const Icon = config.icon;
                const isActive = devRole === role;
                
                return (
                  <button
                    key={role}
                    onClick={() => handleRoleSelect(role)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      borderRadius: '8px',
                      textAlign: 'left',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: isActive ? '#f97316' : 'transparent',
                      color: isActive ? 'white' : '#d1d5db',
                    }}
                  >
                    <Icon className="h-4 w-4" />
                    <span style={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{config.label}</span>
                    {isActive && <LogIn className="h-3 w-3" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Navigation */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '8px', borderTop: '1px solid #374151' }}>
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af', paddingLeft: '4px' }}>Quick Links</span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {linksToShow.map((link) => {
                const Icon = link.icon || LayoutDashboard;
                return (
                  <button
                    key={link.path}
                    onClick={() => handleQuickNav(link.path)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '6px 8px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      border: '1px solid #374151',
                      backgroundColor: 'transparent',
                      color: '#d1d5db',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon style={{ width: '12px', height: '12px' }} />
                    {link.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Role Switcher (when active) */}
          {isDevMode && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid #374151' }}>
              <select
                value={devRole || ''}
                onChange={(e) => handleRoleSelect(e.target.value as DevModeRole)}
                style={{
                  width: '100%',
                  height: '28px',
                  fontSize: '12px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #374151',
                  backgroundColor: '#1f2937',
                  color: '#d1d5db',
                  cursor: 'pointer',
                }}
              >
                <option value="">Switch role...</option>
                {(Object.keys(mockUsers) as DevModeRole[]).map((role) => (
                  <option key={role} value={role}>
                    {mockUsers[role as keyof typeof mockUsers].displayName} ({role})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Help */}
          <div style={{ textAlign: 'center', paddingTop: '4px' }}>
            <p style={{ fontSize: '10px', color: '#9ca3af' }}>
              💡 URL param: <code style={{ backgroundColor: '#374151', padding: '2px 4px', borderRadius: '4px' }}>?devMode={Object.keys(mockUsers)[0]}</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DevModeIndicator: React.FC<DevModeIndicatorProps> = (props) => {
  // Use centralized security check - only shows on localhost and staging
  // DISABLED on: production, preview, and all other remote environments
  if (!isDevModeAllowed()) return null;

  return <DevModeIndicatorInner {...props} />;
};

export default DevModeIndicator;
